import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  AlertActionLink,
  Breadcrumb,
  BreadcrumbItem,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  Spinner,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { DatabaseIcon, ExclamationCircleIcon, SyncAltIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { useBackup } from '../../../hooks/useBackup';
import { useBackupRuns } from '../../../hooks/useBackupRuns';
import { formatBytes } from '../../../utils/formatBytes';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import BackupDiskChainsTable from './BackupDiskChainsTable';
import BackupLatestDomainCard from './BackupLatestDomainCard';
import { renderRunVerificationLabels, renderSubjectStateLabels } from './backupStateLabels';
import { formatBackupAge, formatRunDuration } from './backupTree';

interface BackupRunsTableProps {
  backupId: number;
  /** The subject's `backup_count`, so pagination can render before the first page lands. */
  totalHint: number;
}

/**
 * The subject's full restore-point list, paginated server-side.
 *
 * The detail payload already embeds `recent_runs` (30 by default), but this table owns its own
 * page size and offset, so it always reads the authoritative page from
 * `GET /datacenters/backups/{id}/runs` rather than reconciling two sources of truth.
 *
 * These rows mirror the disk: a run exists exactly while its file does, so the list shrinks as
 * retention prunes. It is **not** a history — nothing here diffs, caches or charts it over time.
 */
const BackupRunsTable: React.FunctionComponent<BackupRunsTableProps> = ({ backupId, totalHint }) => {
  const { runsById, loadingById, errorById, loadRuns } = useBackupRuns();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    loadRuns(backupId, { limit: perPage, offset: (page - 1) * perPage });
  }, [loadRuns, backupId, page, perPage]);

  const requestedOffset = (page - 1) * perPage;
  const pageData = runsById[backupId];
  const isLoading = loadingById[backupId] === true;
  const error = errorById[backupId];
  const itemCount = pageData?.count ?? totalHint;
  // The cache is keyed by subject id alone, so it can legitimately hold a **different** page of
  // the same subject — while a page change is in flight, or after one failed. Rendering those
  // rows under the current paginator would present the wrong restore points as the requested
  // ones, on the very screen whose job is saying which restore points exist. So a cached page
  // counts only when its offset and limit match what is being asked for.
  const isCurrentPage =
    pageData !== undefined && pageData.offset === requestedOffset && pageData.limit === perPage;
  const runs = isCurrentPage ? pageData.runs : [];

  // Restore points disappear as retention prunes them, so `count` can shrink between visits —
  // keep the page in range rather than stranding the reader on an empty offset.
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(itemCount / perPage));
    setPage((current) => Math.min(current, lastPage));
  }, [itemCount, perPage]);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem variant="pagination">
            <Pagination
              itemCount={itemCount}
              perPage={perPage}
              page={page}
              onSetPage={onSetPage}
              onPerPageSelect={onPerPageSelect}
            />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {/* The error is surfaced whenever the last request failed — never suppressed just because
          some other page of this subject happens to be cached. */}
      {error && !isLoading && (
        <Alert
          variant="danger"
          isInline
          title="Failed to load restore points"
          className="pf-v6-u-mb-md"
          actionLinks={
            <AlertActionLink onClick={() => loadRuns(backupId, { limit: perPage, offset: requestedOffset })}>
              Retry
            </AlertActionLink>
          }
        >
          {error.message}
        </Alert>
      )}

      {isLoading && !isCurrentPage ? (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading restore points" />
        </Bullseye>
      ) : runs.length === 0 ? (
        !error && (
          <EmptyState titleText="No restore points" icon={DatabaseIcon} variant="sm" headingLevel="h3">
            <EmptyStateBody>
              There are no restore points on the backup server for this VM right now.
            </EmptyStateBody>
          </EmptyState>
        )
      ) : (
        <Table aria-label="Restore points" variant="compact">
          <Thead>
            <Tr>
              <Th>Restore point</Th>
              <Th>Duration</Th>
              <Th>Metadata size</Th>
              <Th>File</Th>
            </Tr>
          </Thead>
          <Tbody>
            {runs.map((run) => {
              // Badge list, empty today: a future "tested / verified" label belongs to an
              // individual restore point and drops in here without restructuring the table.
              const verificationBadges = renderRunVerificationLabels(run);
              return (
                <Tr key={run.id}>
                  <Td dataLabel="Restore point">
                    <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                      <FlexItem>{formatWallClockDateTime(run.run_at)}</FlexItem>
                      {verificationBadges.map((badge, i) => (
                        <FlexItem key={i}>{badge}</FlexItem>
                      ))}
                    </Flex>
                  </Td>
                  <Td dataLabel="Duration">
                    {/* started_at / ended_at / duration_seconds are a null triple — normal for
                        runs made before the backup process stamped that metadata. */}
                    {run.duration_seconds === null ? (
                      <Tooltip content="This run predates the backup process recording its start and end times.">
                        <span>—</span>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        content={`${formatWallClockDateTime(run.started_at)} → ${formatWallClockDateTime(run.ended_at)}`}
                      >
                        <span>{formatRunDuration(run.duration_seconds)}</span>
                      </Tooltip>
                    )}
                  </Td>
                  <Td dataLabel="Metadata size">{formatBytes(run.size_bytes)}</Td>
                  <Td dataLabel="File">
                    <Content component="small">{run.file}</Content>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}

      {itemCount > 0 && (
        <Pagination
          itemCount={itemCount}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      )}
    </>
  );
};

/**
 * One backup **subject** — a single `<target>/<customer>/<hypervisor>/<vm>` directory — with its
 * disk chains, the libvirt spec captured at the last backup, and the full restore-point list.
 *
 * A **missing** subject opens normally here (with `missing_since` set): `include_missing` governs
 * only the list, so a user following a stored link must still be able to read the row and see why
 * it disappeared. It is never treated as a not-found.
 *
 * Read-only throughout — the resource has no write routes, so there is no restore, delete or
 * "run backup now" affordance anywhere on this page.
 */
function BackupDetail() {
  const auth = useAuth();
  const { backupId: backupIdParam } = useParams<{ backupId: string }>();
  const parsedId = Number(backupIdParam);
  const idIsValid = Number.isInteger(parsedId) && parsedId > 0;

  const { backup, loading, error, refetch } = useBackup(idIsValid ? parsedId : null);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const titleText = backup?.vm_name ?? 'Backup';
  const breadcrumbLeaf = idIsValid ? (backup?.vm_name ?? (loading ? '…' : 'Not found')) : 'Not found';
  const showRefresh = idIsValid && !error && backup !== null;
  const age = backup ? formatBackupAge(backup.hours_since_last_backup) : null;

  const notFound = (
    <EmptyState titleText="Backup not found" icon={ExclamationCircleIcon} variant="lg" headingLevel="h2">
      <EmptyStateBody>
        This backup entry does not exist, or it is not attributed to your organization.
      </EmptyStateBody>
      <EmptyStateFooter>
        <EmptyStateActions>
          <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/backups" />}>
            Back to backups
          </Button>
        </EmptyStateActions>
      </EmptyStateFooter>
    </EmptyState>
  );

  const renderContent = () => {
    if (!idIsValid) {
      return notFound;
    }

    if (loading && backup === null) {
      return (
        <Bullseye>
          <Spinner size="xl" aria-label="Loading backup" />
        </Bullseye>
      );
    }

    if (error) {
      return (
        <Alert
          variant="danger"
          isInline
          title="Failed to load backup"
          actionLinks={<AlertActionLink onClick={() => void refetch()}>Retry</AlertActionLink>}
        >
          {error.message}
        </Alert>
      );
    }

    if (backup === null) {
      return notFound;
    }

    return (
      <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
        {backup.has_warnings && (
          <FlexItem>
            {/* The tenant gets only the boolean; the scanner detail is staff-only. A neutral
                note, deliberately not an alarm, and no cause is invented. */}
            <Alert variant="info" isInline title="Anomalies recorded during the last scan">
              The last scan recorded anomalies in this backup directory. Ancalagon has been notified.
            </Alert>
          </FlexItem>
        )}

        <FlexItem>
          <Card isCompact>
            <CardTitle>Overview</CardTitle>
            <CardBody>
              <DescriptionList isHorizontal isCompact columnModifier={{ default: '1Col', md: '2Col', lg: '3Col' }}>
                <DescriptionListGroup>
                  <DescriptionListTerm>Hypervisor</DescriptionListTerm>
                  <DescriptionListDescription>{backup.hypervisor}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Backup target</DescriptionListTerm>
                  <DescriptionListDescription>{backup.backup_target}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Restore points</DescriptionListTerm>
                  <DescriptionListDescription>{backup.backup_count}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Disk chains</DescriptionListTerm>
                  <DescriptionListDescription>
                    <Tooltip content="Chains, not disks: a disk whose base image was re-seeded has more than one chain.">
                      <span>{backup.disk_count}</span>
                    </Tooltip>
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Size</DescriptionListTerm>
                  <DescriptionListDescription>
                    {formatBytes(backup.size_bytes)}{' '}
                    <Content component="small">({formatBytes(backup.allocated_bytes)} allocated)</Content>
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Last backup</DescriptionListTerm>
                  <DescriptionListDescription>
                    {formatWallClockDateTime(backup.last_backup_at)}
                    {age !== null && <Content component="small"> ({age})</Content>}
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Oldest restore point</DescriptionListTerm>
                  <DescriptionListDescription>
                    {/* Deliberately not "protected since": this describes the oldest *surviving*
                        run, so it moves forward as retention prunes. */}
                    <Tooltip content="The oldest restore point still on the backup server. It moves forward over time as retention prunes older ones.">
                      <span>{formatWallClockDateTime(backup.first_backup_at)}</span>
                    </Tooltip>
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Last seen</DescriptionListTerm>
                  <DescriptionListDescription>
                    {formatWallClockDateTime(backup.last_seen_at)}
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Reported</DescriptionListTerm>
                  <DescriptionListDescription>{formatWallClockDateTime(backup.reported_at)}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Files</DescriptionListTerm>
                  <DescriptionListDescription>
                    {backup.file_count} total{' '}
                    <Content component="small">
                      ({backup.sidecar_count} sidecars, {backup.control_file_count} control files)
                    </Content>
                  </DescriptionListDescription>
                </DescriptionListGroup>
              </DescriptionList>
            </CardBody>
          </Card>
        </FlexItem>

        <FlexItem>
          <Card isCompact>
            <CardTitle>Disk chains</CardTitle>
            <CardBody>
              <BackupDiskChainsTable chains={Array.isArray(backup.disks) ? backup.disks : []} />
            </CardBody>
          </Card>
        </FlexItem>

        <FlexItem>
          <BackupLatestDomainCard latestDomain={backup.latest_domain} />
        </FlexItem>

        <FlexItem>
          <Card isCompact>
            <CardTitle>Restore points</CardTitle>
            <CardBody>
              <Content component="p">
                Every restore point currently on the backup server. Retention is managed by Ancalagon, so this list
                shrinks as older points are pruned.
              </Content>
              <BackupRunsTable backupId={parsedId} totalHint={backup.backup_count} />
            </CardBody>
          </Card>
        </FlexItem>
      </Flex>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/backups">Backups</BreadcrumbItem>
          <BreadcrumbItem isActive>{breadcrumbLeaf}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <DatabaseIcon />
              </Icon>
              {titleText}
            </Content>
            {backup !== null && (
              <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                <FlexItem>
                  <Content component="p">
                    Backed up from {backup.hypervisor} to {backup.backup_target}
                  </Content>
                </FlexItem>
                {/* Retired and Missing are independent states and can appear together. */}
                {renderSubjectStateLabels(backup, null).map((badge, i) => (
                  <FlexItem key={i}>{badge}</FlexItem>
                ))}
              </Flex>
            )}
            {backup === null && <Content component="p">Backup details</Content>}
          </FlexItem>
          {showRefresh && (
            <FlexItem>
              <Button
                variant="secondary"
                icon={<SyncAltIcon />}
                isLoading={loading}
                spinnerAriaValueText="Refreshing backup"
                onClick={() => {
                  if (!loading) {
                    void refetch();
                  }
                }}
              >
                Refresh
              </Button>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
    </>
  );
}

export default BackupDetail;
